import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CITY_NAME, buildSearchPath } from '@/lib/seo';
import { getSiteUrl } from '@/lib/seoConfig';

interface BreadcrumbsProps {
  district?: string;
  specialty?: string;
}

interface Crumb {
  label: string;
  to?: string;
}

// Fil d'Ariane des pages de résultats : Accueil › Rabat › Quartier › Spécialité.
// Renforce le maillage interne (liens vers l'accueil et le niveau quartier) et
// expose un balisage BreadcrumbList pour aider Google à comprendre la hiérarchie.
export default function Breadcrumbs({ district, specialty }: BreadcrumbsProps) {
  const location = useLocation();

  const items = useMemo<Crumb[]>(() => {
    const crumbs: Crumb[] = [
      { label: 'Accueil', to: '/' },
      { label: CITY_NAME, to: '/recherche' },
    ];

    if (district && specialty) {
      // Quartier intermédiaire cliquable, puis la spécialité (page courante).
      crumbs.push({ label: district, to: buildSearchPath(district) });
      crumbs.push({ label: specialty });
    } else if (district) {
      crumbs.push({ label: district });
    } else if (specialty) {
      crumbs.push({ label: specialty });
    } else {
      crumbs.push({ label: 'Tous les praticiens' });
    }

    return crumbs;
  }, [district, specialty]);

  // Injecte le balisage BreadcrumbList (JSON-LD) correspondant au fil affiché.
  useEffect(() => {
    const cleanPath = location.pathname.replace(/\/+$/, '') || '/';
    const siteUrl = getSiteUrl();
    const scriptId = 'breadcrumb-jsonld';
    document.getElementById(scriptId)?.remove();

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: `${siteUrl}${item.to ?? cleanPath}`,
      })),
    });
    document.head.appendChild(script);

    return () => script.remove();
  }, [items, location.pathname]);

  return (
    <nav aria-label="Fil d'Ariane" className="mb-5">
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs md:text-sm text-foreground-600">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-x-1.5">
              {index > 0 && (
                <i className="ri-arrow-right-s-line text-foreground-400 text-base w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              )}
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="inline-flex items-center rounded-sm px-0.5 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="font-medium text-foreground-900 whitespace-nowrap" aria-current="page">
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}